import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CalendarPlus, Search, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/site";
import { todayISO } from "./leadHelpers";

/* Activity types shown in the Schedule modal. Values map 1:1 to backend
 * enum (`call`/`text`/`email`/`meeting`/`in-person`/`zoom`/`other`) but use
 * the user-requested display labels. `zoom` is repurposed as the storage
 * value for "Phone Appointment" since the existing backend enum doesn't
 * have a dedicated phone-appointment value. */
const TYPE_OPTIONS = [
  { v: "call", label: "Follow-up Call" },
  { v: "text", label: "Text Reminder" },
  { v: "email", label: "Email Reminder" },
  { v: "meeting", label: "Meeting" },
  { v: "in-person", label: "In-Person Visit" },
  { v: "zoom", label: "Phone Appointment" },
  { v: "other", label: "Other Task" },
];

/* Schedule Activity modal — accessible from the Calendar tab.
 * Required: lead selection (searchable). General Task is intentionally
 * deferred (would require a separate data model — see PRD backlog). */
export default function ScheduleActivityModal({ open, leads, onClose, onCreated }) {
  const [leadId, setLeadId] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("call");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);

  // Reset form whenever the modal is reopened.
  useEffect(() => {
    if (!open) return;
    setLeadId("");
    setSearch("");
    setType("call");
    setDate(todayISO());
    setTime("");
    setNote("");
    setBusy(false);
    setSearchOpen(false);
  }, [open]);

  // Close picker on outside click.
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onClick = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [searchOpen]);

  const selectedLead = useMemo(
    () => (leads || []).find((l) => l.id === leadId) || null,
    [leadId, leads],
  );

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const active = (leads || []).filter((l) => !l.is_archived && !l.is_trashed);
    if (!q) return active.slice(0, 25);
    return active
      .filter((l) => {
        const hay = `${l.business_name || ""} ${l.contact_name || ""} ${l.email || ""} ${l.phone || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 25);
  }, [leads, search]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!leadId) { toast.error("Please pick a lead"); return; }
    if (!date) { toast.error("Please pick a date"); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_BASE}/admin/leads/${leadId}/scheduled-activity`,
        { type, date, time: time || null, note: note || null },
        { withCredentials: true },
      );
      const activity = r.data?.activity;
      onCreated?.(leadId, activity);
      toast.success("Activity scheduled");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule activity");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-activity-title"
      data-testid="schedule-activity-modal"
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 sm:p-6"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="fixed inset-0 bg-[var(--swipe-navy)]/45 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center">
              <CalendarPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 id="schedule-activity-title" className="font-display text-[16px] font-extrabold text-[var(--swipe-navy)] leading-tight">
                Schedule Activity
              </h2>
              <div className="text-[11.5px] text-slate-500">
                Pick a lead and a date — saved as Pending.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="schedule-activity-close"
            aria-label="Close modal"
            className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Lead picker */}
          <div ref={searchWrapRef} className="relative">
            <label className="block text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1.5">
              Lead <span className="text-rose-600">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={selectedLead && !searchOpen ? `${selectedLead.business_name || selectedLead.contact_name || "Unnamed"} — ${selectedLead.email || selectedLead.phone || ""}` : search}
                onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); if (selectedLead) setLeadId(""); }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search business name, contact, email, or phone…"
                data-testid="schedule-activity-lead-search"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/20 text-[13.5px] text-[var(--swipe-navy)] outline-none transition-colors"
              />
            </div>
            {searchOpen ? (
              <div className="absolute z-10 mt-1 left-0 right-0 max-h-56 overflow-y-auto bg-white rounded-lg border border-slate-200 shadow-lg" data-testid="schedule-activity-lead-results">
                {filteredLeads.length === 0 ? (
                  <div className="px-3 py-3 text-[12.5px] text-slate-400 italic">No matching leads.</div>
                ) : (
                  filteredLeads.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => { setLeadId(l.id); setSearch(""); setSearchOpen(false); }}
                      data-testid={`schedule-activity-lead-option-${l.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--swipe-accent-soft)]/40 border-b border-slate-100 last:border-0"
                    >
                      <div className="font-display font-bold text-[13px] text-[var(--swipe-navy)] truncate">
                        {l.business_name || l.contact_name || "Unnamed lead"}
                      </div>
                      <div className="text-[11.5px] text-slate-500 truncate">
                        {l.contact_name && l.business_name ? l.contact_name + " · " : ""}
                        {l.email || l.phone || ""}
                      </div>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          {/* Activity type */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1.5">
              Activity type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="schedule-activity-type"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/20 text-[13.5px] text-[var(--swipe-navy)] outline-none"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1.5">
                Date <span className="text-rose-600">*</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="schedule-activity-date"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/20 text-[13.5px] text-[var(--swipe-navy)] outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1.5">
                Time
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="schedule-activity-time"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/20 text-[13.5px] text-[var(--swipe-navy)] outline-none"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1.5">
              Notes
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="What's this activity about? (optional)"
              data-testid="schedule-activity-note"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/20 text-[13.5px] text-[var(--swipe-navy)] outline-none resize-none"
            />
          </div>

          {/* Status hint — fixed to Pending per spec */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11.5px] text-slate-600">
            <span className="font-bold text-[var(--swipe-navy)]">Status:</span> Pending (you can mark complete from the Calendar later).
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid="schedule-activity-cancel"
              className="px-4 h-10 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 text-[13px] font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !leadId}
              data-testid="schedule-activity-submit"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[var(--swipe-accent)] hover:bg-[var(--swipe-accent)]/90 text-white text-[13px] font-bold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
              {busy ? "Scheduling…" : "Schedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
